import { ISettingsProps } from "./Models.ts";
import { ProblemFormatOptions } from "./Constants.ts";
import type { SheetSpec } from "./PdfGenerator/sheetSpec.ts";
import { ladderForSettings, levelApplies } from "./FactLevels.ts";

/**
 * A class roster, typed one name per line (ADR 0011). It never leaves the
 * browser — not localStorage, not sessionStorage, not the URL — so everything
 * here works on a plain string held in component state.
 */

/** The most names a roster may hold; a guard against pasting a whole year group. */
export const MAX_ROSTER = 40;

/** One roster line: the name that prints, and the level typed after it, if any (ADR 0071 §6). */
export interface RosterEntry {
    name: string;
    /** 1 or more, as typed; whether the ladder has it is decided where the sheet is built. */
    level: number | null;
}

export interface Roster {
    /** Trimmed, blanks dropped, capped at MAX_ROSTER. Duplicates kept. */
    entries: RosterEntry[];
    /** `entries`' names, for the callers that only print them. */
    names: string[];
    /** True when the input held more than MAX_ROSTER names and some were dropped. */
    overflowed: boolean;
}

/**
 * A trailing level follows the last comma or tab on the line: "Maya, 6", or "Maya" and "6" pasted
 * from two spreadsheet columns. A plain space is never a separator, so "Ryan 2" is a name, and so
 * is "Smith, John", which has no number after its comma.
 */
const TRAILING_LEVEL = /^(.*?)\s*[,\t]\s*(\d{1,3})\s*$/;

/**
 * The level is read off whether or not levels are on. A roster typed as "Maya, 6" on an ordinary
 * sheet would otherwise print "Maya, 6" in the header, which is worse than dropping the number;
 * this is the one change an ordinary class set sees. 0 is not a level, so "Maya, 0" is Maya.
 */
function parseLine(line: string): RosterEntry {
    const match = line.match(TRAILING_LEVEL);
    if (!match) {
        return { name: line, level: null };
    }
    const level = Number(match[2]);
    return { name: match[1].trim(), level: level > 0 ? level : null };
}

export function parseRoster(text: string): Roster {
    const all = text
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .map(parseLine)
        // ", 6" has a level and no name: a blank line, like any other.
        .filter((entry) => entry.name.length > 0);
    const entries = all.slice(0, MAX_ROSTER);
    return {
        entries,
        names: entries.map((entry) => entry.name),
        overflowed: all.length > MAX_ROSTER,
    };
}

/**
 * The level a student's sheet is, and whether the roster's own number was usable. A level the
 * ladder lacks (12 on an 11-level ladder) falls back to the sheet's level rather than to 1: the
 * sheet's level is the one the teacher can see on screen.
 */
export function studentLevel(
    entry: RosterEntry,
    settings: ISettingsProps
): { level: number; outOfRange: boolean } {
    const ladder = ladderForSettings(settings);
    const sheetLevel = Math.min(settings.FactLevel, ladder ? ladder.levels.length : settings.FactLevel);
    if (entry.level === null || !ladder) {
        return { level: sheetLevel, outOfRange: false };
    }
    if (entry.level > ladder.levels.length) {
        return { level: sheetLevel, outOfRange: true };
    }
    return { level: entry.level, outOfRange: false };
}

/** How many lines name a level past the end of a ladder with `ladderLength` levels. */
export function countOutOfRange(entries: RosterEntry[], ladderLength: number): number {
    return entries.filter((entry) => entry.level !== null && entry.level > ladderLength).length;
}

/** How many roster lines carry a level the ladder does not have; 0 when levels are not on. */
export function outOfRangeLevels(entries: RosterEntry[], settings: ISettingsProps): number {
    if (!levelApplies(settings)) {
        return 0;
    }
    return countOutOfRange(entries, ladderForSettings(settings)!.levels.length);
}

/**
 * The documents a class set prints.
 *
 * - **Same problems** (default): one worksheet per student, all from the same
 *   seed, then a single answer key at the end.
 * - **Different problems**: each student gets their own seed (`seed:index`, not
 *   a fresh random — the set still rebuilds from one footer ID, and a renamed
 *   entry does not change anyone's sheet) and their own key, name in the header.
 */
export function classSetSheets(
    settings: ISettingsProps,
    seed: string,
    roster: (RosterEntry | string)[]
): SheetSpec[] {
    const entries = roster.map((entry) => (typeof entry === 'string' ? { name: entry, level: null } : entry));
    if (entries.length === 0) {
        return [{ settings, seed }];
    }

    // On a fact fluency level test (ADR 0071 §6) each line may name its own level; with no ladder
    // the numbers are ignored and every sheet is the sheet on screen.
    const levels = levelApplies(settings)
        ? entries.map((entry) => studentLevel(entry, settings).level)
        : entries.map(() => null);
    const sheetLevel = levelApplies(settings) ? Math.min(settings.FactLevel, ladderForSettings(settings)!.levels.length) : null;
    const settingsAt = (level: number | null): ISettingsProps =>
        level === null || level === sheetLevel ? settings : { ...settings, FactLevel: level };

    if (settings.ClassSetDistinctProblems) {
        return entries.map((entry, index) => ({
            settings: settingsAt(levels[index]),
            seed: `${seed}:${index}`,
            footerSeed: `${seed}:${index}`,
            studentName: entry.name,
        }));
    }

    // Same problems: students on the sheet's own level draw from the base seed, so their sheet is
    // the preview's, as 0010's middle tier is. Students on another level draw from `seed:L6`, and
    // print `seed-L6` - which, like `-1` on a differentiation tier and `:3` on a distinct class
    // set, rebuilds the base sheet when typed into Rebuild, not that level's.
    const seedAt = (level: number | null) => (level === null || level === sheetLevel ? seed : `${seed}:L${level}`);
    const footerAt = (level: number | null) => (level === null || level === sheetLevel ? seed : `${seed}-L${level}`);
    const worksheets: SheetSpec[] = entries.map((entry, index) => ({
        settings: settingsAt(levels[index]),
        seed: seedAt(levels[index]),
        footerSeed: footerAt(levels[index]),
        studentName: entry.name,
        suppressKey: true,
    }));
    if (settings.AnswerPages) {
        // One key per level on the roster, in ladder order, after every worksheet.
        const present = [...new Set(levels)].sort((a, b) => (a ?? 0) - (b ?? 0));
        for (const level of present) {
            worksheets.push({
                settings: settingsAt(level),
                seed: seedAt(level),
                footerSeed: footerAt(level),
                keyOnly: true,
            });
        }
    }
    return worksheets;
}

/**
 * How many printed pages a spec list comes to, from the settings alone - a
 * class set, a differentiation set's three tiers, or one plain sheet, answer
 * pages included.
 *
 * Exact except for a worked answer key, which re-flows to its own density and
 * so depends on the problems drawn (ADR 0015 §4). The workbench shows this
 * only until the build finishes, then the count read from the PDF itself
 * (Shared/Pdf/pdfPageCount.ts).
 */
export function pageCount(sheets: SheetSpec[]): number {
    let total = 0;
    for (const spec of sheets) {
        // A substitute pack's cover (ADR 0083) and a placement check's cover and last page (ADR 0086)
        // are one page each, and have no settings to read.
        if (spec.cover || spec.placementCover || spec.catchUpCover || spec.bundleCover || spec.whereToStart) {
            total += 1;
            continue;
        }
        const pages = spec.settings.NumberOfPages || 1;
        if (!spec.keyOnly) total += pages;
        if (spec.settings.AnswerPages && !spec.suppressKey) total += pages;
    }
    return total;
}

/**
 * How many problems a spec list gives students to answer: the per-page count
 * times the pages for every sheet, twice over on a half page, where each half
 * is a set of its own (Pages.tsx). A class set's shared key answers problems
 * already counted, so it adds none.
 */
export function problemCount(sheets: SheetSpec[]): number {
    let total = 0;
    for (const spec of sheets) {
        if (spec.keyOnly || spec.cover || spec.placementCover || spec.catchUpCover || spec.bundleCover || spec.whereToStart) continue;
        const { settings } = spec;
        // A color-by-number picture has no half-page size (ADR 0058 §5).
        const halves =
            settings.HalfPage &&
            settings.Format !== ProblemFormatOptions.ColorByNumber &&
            settings.Format !== ProblemFormatOptions.RiddleDecoder
            ? 2
            : 1;
        total += settings.NumberOfProblemsPerPage * (settings.NumberOfPages || 1) * halves;
    }
    return total;
}
